import {Flex, Stack} from "../../../../shared/layouts";
import Card from "../../../../shared/Card";
import {PureDynamicForm} from "@dnhb/functional-final-form";
import axios from "axios";
import {
    CheckBoxListField,
    DateField,
    InputField,
    RadioButtonField,
    TextAreaField
} from "../../../../shared/final-form-fields";
import FormList from "../../questionnaires/FormList";
import Button from "../../../../shared/Button";
import * as React from "react";
import {useMediaQuery} from "@dnhb/node_package";
import {useContext, useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate, useParams} from "react-router-dom";
import {useTheme} from '@emotion/react';
import MessagebarContext from "../../../../../context/MessagebarContext";
import NameHeader from "../../../tools/maxMortgage/components/NameHeader";
import SelectField from "../../../../shared/final-form-fields/SelectField";
import AddItemBox from "../../../../shared/AddItemBox";
import GenericCollapse from "../components/GenericCollapse";

type DataV2Type = {
    definition: {
        completionMessage: string;
        questions: any[];
    };
    answers: any[];
    current_page_index: number;
}

const FinlyDynamicForm = ({data,}: { data: DataV2Type }) => {
    const {t} = useTranslation();
    const {finlyFormId} = useParams();
    const {breakpoints} = useTheme();

    const isMinTablet = useMediaQuery(
        `(min-width: ${breakpoints.md ?? '37.5rem'})`
    );
    const formRef = useRef(null);

    const [pageIndex, setPageIndex] = useState(data.current_page_index);
    const [totalPages, setTotalPages] = useState(0);
    const lastQuestion = pageIndex === totalPages - 1;
    const navigate = useNavigate();
    const {setSuccesMessage} = useContext(MessagebarContext);

    useEffect(() => {
        const pagesAmount = data.definition.questions.filter(({formType}) => formType === 'page')
            ?.length;
        const maxIndex = pagesAmount - 1;
        setPageIndex(data.current_page_index > maxIndex ? maxIndex : data.current_page_index);

        setTotalPages(pagesAmount);
    }, [data.answers]);

    const calculatePageSkip = (back = false) => {
        const addition = back ? -1 : 1;
        let pageSkip = 0;
        let nextPage = formRef?.current?.children?.[pageIndex + addition];
        while (nextPage && nextPage.style.getPropertyValue('display') === 'none') {
            pageSkip += addition;
            nextPage = formRef?.current?.children[pageIndex + addition + pageSkip];

        }
        return Math.abs(pageSkip);
    }


    const addRepeatableEntity = (props, e) => {
        props.onClick(e);
        axios.post(`/finly-form/${finlyFormId}/addRepeatableEntity`, {
            entity_index: props.totalIndexes,
            entity: props.name,
            group_id: Number(props.name.replace('e_', ''))
        });
    }

    const removeRepeatableEntity = (props) => {
        props.onDelete();
        const regex = /(\d+)\[(.*?)\]/;
        const match = regex.exec(props.name);
        axios.delete(`/finly-form/${finlyFormId}/removeRepeatableEntity`, {
            data: {
                entity_index: Number(match[2]),
                entity: `e_${match[1]}`,
                group_id: Number(match[1])
            }
        });
    }


    return (
        <Card>
            <PureDynamicForm
                ref={formRef}
                axiosConfig={{
                    method: 'PUT',
                    transformRequest: [
                        data => ({...data, pageIndex, pageSkip: calculatePageSkip()}),
                        ...axios.defaults.transformRequest as any
                    ]
                }}
                url={`/finly-form/${finlyFormId}`}
                afterSubmit={({nextPageIndex}) => {
                    if (lastQuestion) {
                        setSuccesMessage(data.definition.completionMessage ?? t('Vragenlijst afgerond'));
                        navigate('/requests');
                    } else {
                        setPageIndex(index => nextPageIndex || index + 1 + calculatePageSkip());
                    }
                }}
                formDefinition={data.definition?.questions}
                initialValues={data.answers}
                elementMap={{
                    text: InputField,
                    number: props => (
                        <InputField name={props.name} {...props} inputMode="numeric"/>
                    ),
                    currency: props => <InputField name={props.name} {...props} isCurrency={'1'}/>,
                    date: DateField,
                    textarea: TextAreaField,
                    checkbox: CheckBoxListField,
                    radio: props => <RadioButtonField name={props.name} options={[]} {...props} />,
                    select: props => <SelectField {...props} isSearchable/>,
                    page: props => {
                        if (props?.name === `page_${pageIndex}`) {
                            return <Stack  {...props} space={'xl'}/>;
                        }
                        return null;
                    },
                    header: NameHeader,
                    category: GenericCollapse,
                    paragraph: props => <p>{props.value}</p>,
                    AddButton: props => (
                        <AddItemBox
                            title={`${props?.item} toevoegen`}
                            {...props}
                            onClick={(e) => addRepeatableEntity(props, e)}
                        />
                    ),
                    list: props =>
                        <FormList {...props} allowToDeleteOnlyLastElement
                                  onDelete={() => removeRepeatableEntity(props)}/>,
                }}
            />

            <Flex
                mt="xl"
                justifyContent="space-between"
                flexDirection={isMinTablet ? 'row-reverse' : 'column'}
                css={{'> * + *': {marginTop: !isMinTablet ? '16px' : null}}}
            >
                {/* Buttons for testing */}
                <Button
                    variant="primary"
                    buttonSize="small"
                    width={!isMinTablet ? '100%' : null}
                    onClick={() =>
                        pageIndex < 0
                            ? setPageIndex(0)
                            : formRef.current.requestSubmit()
                    }
                >
                    {lastQuestion
                        ? t('Verstuur en sluit af')
                        : pageIndex < 0
                            ? t('Start')
                            : t('Volgende')}
                </Button>
                {pageIndex > 0 && (
                    <Button
                        variant="primary-outline"
                        buttonSize="small"
                        width={!isMinTablet ? '100%' : null}
                        onClick={() => setPageIndex(index => index - 1 - calculatePageSkip(true))}
                    >
                        {t('Vorige')}
                    </Button>
                )}
            </Flex>
        </Card>
    )
}
export default FinlyDynamicForm;
